import { Shield, Users, Truck, Clock, DollarSign, Award, MapPin, HeartHandshake, Camera, PackageCheck, Recycle, Zap, ChevronRight } from "lucide-react";
import ServicePageLayout from "@/components/ServicePageLayout";

const services = [
  { slug: "household-rubbish-removal", title: "Household Rubbish Removal", desc: "General junk, clutter and unwanted household items cleared fast." },
  { slug: "furniture-removal", title: "Furniture Removal", desc: "Sofas, beds, wardrobes and bulky items removed without the hassle." },
  { slug: "move-out-clear-outs", title: "Move-Out Clear-Outs", desc: "End of lease and pre-sale clear-outs done properly." },
  { slug: "garage-shed-clear-outs", title: "Garage & Shed Clear-Outs", desc: "Reclaim your garage or shed, from a few items to a full clear." },
  { slug: "commercial-rubbish-removal", title: "Office & Commercial", desc: "Office furniture, fit-outs and commercial waste removed on schedule." },
  { slug: "property-clear-outs", title: "Property Clear-Outs", desc: "Full property clearances handled with care and efficiency." },
];

const ServicesBlock = (
  <section className="py-20 md:py-28 bg-white">
    <div className="container">
      <h2 className="text-3xl md:text-4xl font-black text-[#1a1a1a] text-center mb-14 tracking-tight">
        Services Available in Melbourne
      </h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
        {services.map((s) => (
          <a
            key={s.slug}
            href={`/${s.slug}`}
            className="group bg-white/70 backdrop-blur-xl border border-[#1a1a1a]/10 shadow-[0_8px_30px_rgba(26,26,26,0.06)] rounded-2xl p-6 hover:border-[#1a1a1a]/30 transition-colors"
          >
            <div className="flex items-center gap-2 text-[#1a1a1a] font-bold text-lg">
              {s.title}
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </div>
            <p className="text-[#1a1a1a]/70 text-sm mt-2 leading-relaxed">{s.desc}</p>
          </a>
        ))}
      </div>
    </div>
  </section>
);

const MelbournePage = () => (
  <ServicePageLayout
    slug="melbourne"
    title="Rubbish Removal Melbourne: Fast & Upfront Pricing | OZ WASTE CO."
    metaDescription="Rubbish removal across Melbourne. We do the lifting, loading and removal, you just show us what needs to go. Upfront quotes from OZ WASTE CO."
    h1="Rubbish Removal Melbourne"
    subtitle="Fast rubbish removal across Melbourne. We do the lifting, loading and removal, you just show us what needs to go."
    breadcrumbLabel="Melbourne"
    breadcrumbParent={{ label: "Locations", href: "/locations" }}
    schemaServiceType="Rubbish Removal Services"
    areaServed={["Melbourne"]}
    aboutHeading="Rubbish Removal in Melbourne"
    overview={[
      "OZ WASTE CO. is a Melbourne based rubbish removal team working right across the metro area, from the CBD and inner suburbs through to Doncaster, Point Cook, Brighton and Frankston.",
      "Household junk, old furniture, garage clear-outs, office waste, you point, we load, it's gone. No skip bin sitting on your nature strip for a week and no heavy lifting on your end.",
      "Every job comes with an upfront quote and a friendly local crew. You know the agreed price before we start, and we leave the area clean when we're done.",
    ]}
    includedHeading="Included on Every Melbourne Job"
    included={[
      { icon: Users, label: "Friendly Local Crew" },
      { icon: Truck, label: "All Loading Done For You" },
      { icon: PackageCheck, label: "Careful Removal" },
      { icon: DollarSign, label: "Upfront Pricing" },
      { icon: Camera, label: "Easy Photo Quotes" },
      { icon: Recycle, label: "Responsible Disposal" },
      { icon: Clock, label: "Fast Pickup Windows" },
      { icon: Shield, label: "Fully Insured" },
    ]}
    whyUsHeading="Why Choose OZ WASTE CO. in Melbourne"
    whyUs={[
      { icon: MapPin, title: "Melbourne Owned", desc: "A local rubbish removal team, not an interstate call centre or franchise." },
      { icon: DollarSign, title: "Upfront Quotes", desc: "Know the agreed price before the job begins, no surprises." },
      { icon: Truck, title: "We Do the Lifting", desc: "No dragging everything outside or loading a skip yourself." },
      { icon: Camera, title: "Photo Quotes", desc: "Send a few photos so we can assess the job quickly." },
      { icon: Zap, title: "Fast Pickup", desc: "Same-day and short-notice pickups available depending on the schedule." },
      { icon: Recycle, title: "Responsible Disposal", desc: "Reusable and recyclable items are diverted from landfill where possible." },
      { icon: Clock, title: "Reliable Timing", desc: "Realistic pickup windows we actually stick to." },
      { icon: HeartHandshake, title: "Friendly Service", desc: "Calm, tidy and respectful from first call to final sweep." },
    ]}
    extra={ServicesBlock}
    faqs={[
      { q: "Which Melbourne suburbs do you service?", a: "All of them, from the CBD, Southbank and Docklands through the inner north and east, out to Werribee, Craigieburn, Ringwood, Dandenong and Frankston." },
      { q: "How much does rubbish removal cost in Melbourne?", a: "Pricing depends on the volume and type of rubbish. Send us a few photos or a quick description and we'll confirm an upfront quote before we start." },
      { q: "Do I need to move everything outside first?", a: "No. Our crew does all the lifting and loading from wherever the items are, inside the house, the garage, the backyard or the office." },
      { q: "What items do you take?", a: "Most household and commercial items: furniture, appliances, mattresses, boxes, green waste, office furniture and general junk. Some hazardous items are excluded, see our Special Items section or ask us." },
      { q: "Can you do same-day pickups?", a: "Often, yes. Same-day and short-notice pickups may be available depending on the day's schedule, get in touch early for the best chance." },
      { q: "How is this different from hiring a skip bin?", a: "With a skip you do the loading, pay for the whole bin and wait days for pickup. With us, we arrive, load everything for you and take it away in one visit, you only pay for the space your rubbish takes up." },
      { q: "Are you insured?", a: "Yes. Public liability cover applies to every Melbourne job, so your property is protected while we work." },
    ]}
  />
);

export default MelbournePage;
